import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { CsatContext, RatingKey } from '../lib/types';

/**
 * The page a customer lands on after clicking a face in a ticket email.
 *
 * The only page in this app a person outside the company ever sees, and the
 * only one that renders without a session. It deliberately carries none of the
 * app's chrome — no sidebar, no nav, no sign-out — because to the customer
 * this is not an app, it is a two-field form that appeared out of an email.
 *
 * `d` and `s` are opaque here: the signed payload and its HMAC. The browser
 * cannot read or validate them, which is the point — the API is the only thing
 * that can, and it re-checks on both the load and the submit.
 */

const FACES: { key: RatingKey; label: string; glyph: string }[] = [
  { key: 'green', label: 'Happy', glyph: '🙂' },
  { key: 'yellow', label: 'Okay', glyph: '😐' },
  { key: 'red', label: 'Unhappy', glyph: '🙁' },
];

/* Mirrors the API's messages, for the one case that never reaches the API: the
   /r redirect already found the link bad and said so in the query string, so
   spending a round trip to be told again would just be slower. */
const LINK_MESSAGES: Record<string, string> = {
  missing: 'This feedback link is incomplete. Please use the link from your ticket email.',
  'bad-signature': 'This feedback link is not valid. Please use the link from your ticket email.',
  malformed: 'This feedback link could not be read. Please use the link from your ticket email.',
  expired: 'This feedback link has expired. Please reply to your ticket instead.',
};

const MAX_NOTES = 4000;

/**
 * The parameters that identify this link, whichever kind it is.
 *
 * A per-ticket link is `d` + `s`. A template one is `v` plus the plain ticket
 * fields the PSA merged in, and is recognised by carrying `v` — which has to be
 * tested BEFORE `s`, because on a template link `s` is the ticket subject
 * rather than a signature.
 *
 * Whatever this returns is passed straight back to the API on submit, so the
 * page never has to know which kind it is holding beyond this one function.
 */
function linkParams(params: URLSearchParams): Record<string, string> | null {
  if (params.get('v')) {
    const out: Record<string, string> = { v: params.get('v')! };
    for (const key of ['t', 'c', 'a', 'k', 's']) {
      const value = params.get(key);
      if (value) out[key] = value;
    }
    return out;
  }

  const d = params.get('d');
  const s = params.get('s');
  return d && s ? { d, s } : null;
}

export function CsatFeedbackPage() {
  const params = new URLSearchParams(window.location.search);
  const link = linkParams(params);
  const redirectError = params.get('error');

  const [context, setContext] = useState<CsatContext | null>(null);
  const [rating, setRating] = useState<RatingKey | null>(null);
  const [notes, setNotes] = useState('');
  const [phase, setPhase] = useState<'loading' | 'form' | 'sending' | 'done' | 'error'>(
    redirectError ? 'error' : 'loading',
  );
  const [message, setMessage] = useState<string | null>(
    redirectError ? (LINK_MESSAGES[redirectError] ?? LINK_MESSAGES.missing!) : null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const notesRef = useRef<HTMLTextAreaElement>(null);

  /* Serialised once and depended on as a string: `link` is rebuilt on every
     render, so using the object itself would re-run the fetch forever. */
  const linkQuery = link ? new URLSearchParams(link).toString() : null;

  useEffect(() => {
    if (redirectError) return;
    if (!linkQuery) {
      setMessage(LINK_MESSAGES.missing!);
      setPhase('error');
      return;
    }

    let cancelled = false;
    api
      .get<CsatContext>(`/csat/public/context?${linkQuery}`)
      .then((result) => {
        if (cancelled) return;
        setContext(result);
        setRating(result.rating);
        setPhase('form');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMessage(
          err instanceof ApiError && err.status !== 0
            ? err.message
            : 'We could not reach the feedback service. Please try again in a moment.',
        );
        setPhase('error');
      });

    return () => {
      cancelled = true;
    };
  }, [linkQuery, redirectError]);

  /* Land the customer in the box they came here to fill in, but do not yank
     the page around on a phone. */
  useEffect(() => {
    if (phase === 'form' && window.matchMedia('(min-width: 640px)').matches) {
      notesRef.current?.focus();
    }
  }, [phase]);

  const submit = async () => {
    if (!rating) {
      setFormError('Please choose a rating.');
      return;
    }
    setFormError(null);
    setPhase('sending');
    try {
      await api.post('/csat/public/feedback', { ...link, rating, notes });
      setPhase('done');
    } catch (err) {
      setPhase('form');
      setFormError(
        err instanceof ApiError && err.status !== 0
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    }
  };

  return (
    <div className="csat-public">
      <main className="csat-public-card">
        {phase === 'loading' && (
          <div className="loading">
            <span className="spinner" aria-hidden="true" />
            <span>Loading…</span>
          </div>
        )}

        {(phase === 'form' || phase === 'sending') && (
          <>
            <header className="csat-public-head">
              <h1>How did we do?</h1>
              <p className="sub">
                {[context?.ticketId && `Ticket ${context.ticketId}`, context?.subject]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </header>

            <fieldset className="csat-faces">
              <legend className="visually-hidden">Choose a rating</legend>
              {FACES.map((face) => (
                <label
                  key={face.key}
                  className={`csat-face csat-face--${face.key}${rating === face.key ? ' is-on' : ''}`}
                >
                  <input
                    type="radio"
                    name="rating"
                    value={face.key}
                    checked={rating === face.key}
                    onChange={() => setRating(face.key)}
                  />
                  {/* The same emoji the customer clicked in the ticket. Email
                      cannot use images or SVG, so the page matches the email
                      rather than the other way round. */}
                  <span className="csat-face-glyph" aria-hidden="true">
                    {face.glyph}
                  </span>
                  <span className="csat-face-label">{face.label}</span>
                </label>
              ))}
            </fieldset>

            <label className="csat-notes-label" htmlFor="csat-notes">
              Anything you&rsquo;d like to add?
              <span className="xs subtle">optional</span>
            </label>
            <textarea
              id="csat-notes"
              ref={notesRef}
              className="textarea"
              rows={5}
              maxLength={MAX_NOTES}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Tell us what went well, or what we could do better."
            />
            <div className="xs subtle right">
              {notes.length} / {MAX_NOTES}
            </div>

            {formError && (
              <div className="alert mt-4" role="alert">
                {formError}
              </div>
            )}

            <button
              type="button"
              className="btn btn-primary csat-submit mt-4"
              disabled={phase === 'sending'}
              onClick={() => void submit()}
            >
              {phase === 'sending' ? 'Sending…' : 'Submit feedback'}
            </button>
          </>
        )}

        {phase === 'done' && (
          <div className="csat-public-result">
            <svg viewBox="0 0 48 48" width="56" height="56" aria-hidden="true">
              <circle cx="24" cy="24" r="21" fill="none" stroke="currentColor" strokeWidth="3" />
              <path
                d="M15 24.5l6.5 6.5L33 19"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <h1>Thank you</h1>
            <p className="sub">Your feedback has been sent to our team.</p>
          </div>
        )}

        {phase === 'error' && (
          <div className="csat-public-result is-bad">
            <svg viewBox="0 0 48 48" width="56" height="56" aria-hidden="true">
              <circle cx="24" cy="24" r="21" fill="none" stroke="currentColor" strokeWidth="3" />
              <path d="M24 13v15" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" />
              <circle cx="24" cy="34.5" r="2.2" fill="currentColor" />
            </svg>
            <h1>We couldn&rsquo;t open this link</h1>
            <p className="sub">{message}</p>
          </div>
        )}
      </main>
      <p className="csat-public-foot xs subtle">Powered by JCurve Operations</p>
    </div>
  );
}
